import { EditPermissions } from '../../../../../../../shared/models';
import { getEditTypes } from '../../../delegatorUtils';
import { newStepValue } from '../../../../../services/flowBuilder/utils/constants';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { required, expiryDaysRange } from '../../../../../../../shared/utils';

const SECONDS_PER_DAY = 86400;
const EXPIRY_MIN_DAYS = 1;
const EXPIRY_MAX_DAYS = 30;

export interface RedisData {
  name: string,
  description: string,
  redisAction: string,
  yes: string,
  no: string
}

export interface LogicData {
  key: string,
  expire: string,
  value: string,
}

export interface RedisForms {
  RedisForm: UntypedFormGroup,
  LogicForm: UntypedFormGroup
}

export enum ActionType {
  DECREMENT = 'decr',
  DECREMENT_BY = 'decrBy',
  DELETE_ = 'del',
  EXIST = 'exist',
  GET_ = 'get',
  INCREMENT = 'incr',
  INCREMENT_BY = 'incrBy',
  SET = 'set',
}

export class Redis {

  action: any = {};

  constructor(formData: RedisData, logicData: LogicData) {
    this.action = {
      'actionType': 'redis',
    };
    this.setNodeData(formData, logicData);
  }

  static toForm(stepData: { [index: string]: any }, editPermissions: EditPermissions): RedisForms {
    const { editMenuTextOnly, editFlows } = getEditTypes(editPermissions);
    const formData = this.getFromData(stepData);
    const logicData = this.getLogicData(stepData);

    return {
      RedisForm: this.buildForm(formData, editFlows),
      LogicForm: this.buildLogicForm(logicData, formData.redisAction, editFlows),
    };
  }

  private static getFromData(stepData: { [index: string]: any }): RedisData {
    const action = stepData?.action ? stepData?.action : {};
    const actionParams = action?.actionParams ? action?.actionParams : {};
    const result = action?.result ? action?.result : {};

    return {
      name: action.name ? action.name : '',
      description: action.description ? action.description : '',
      redisAction: actionParams.redisAction ? actionParams.redisAction : '',
      yes: result.yes ? result.yes : newStepValue,
      no: result.no ? result.no : newStepValue,
    };
  }

  private static buildForm(formData: RedisData, editFlows: boolean): UntypedFormGroup {
    return new UntypedFormGroup({
      name: new UntypedFormControl({
        value: formData.name,
        disabled: !editFlows,
      }, required('Name')),
      description: new UntypedFormControl({
        value: formData.description,
        disabled: !editFlows,
      }, required('Description')),
      redisAction: new UntypedFormControl({
        value: formData.redisAction,
        disabled: !editFlows,
      }, required('Redis Action')),
      yes: new UntypedFormControl(formData.yes),
      no: new UntypedFormControl(formData.no),
    });
  }

  private static getLogicData(stepData: { [index: string]: any }): LogicData {
    const action = stepData?.action ? stepData?.action : {};
    const actionParams = action?.actionParams ? action?.actionParams : {};

    const expireSeconds = actionParams.expire ? actionParams.expire : '';
    const expire = expireSeconds && !isNaN(Number(expireSeconds))
      ? String(Math.round(Number(expireSeconds) / SECONDS_PER_DAY))
      : '';

    return {
      key: actionParams.key ? actionParams.key : '',
      value: actionParams.value ? actionParams.value : '',
      expire,
    };
  }

  public static buildLogicForm(formData: LogicData, actionType: String, editFlows: boolean): UntypedFormGroup {
    switch (actionType) {
      case ActionType.DECREMENT:
        return new UntypedFormGroup({
          key: new UntypedFormControl({
            value: formData.key,
            disabled: !editFlows,
          }, required('Key')),
          expire: new UntypedFormControl({
            value: formData.expire,
            disabled: !editFlows,
          }, [required('Expire'), expiryDaysRange(EXPIRY_MIN_DAYS, EXPIRY_MAX_DAYS)]),
        });
      case ActionType.DECREMENT_BY:
        return new UntypedFormGroup({
          key: new UntypedFormControl({
            value: formData.key,
            disabled: !editFlows,
          }, required('Key')),
          expire: new UntypedFormControl({
            value: formData.expire,
            disabled: !editFlows,
          }, [required('Expire'), expiryDaysRange(EXPIRY_MIN_DAYS, EXPIRY_MAX_DAYS)]),
          value: new UntypedFormControl({
            value: formData.value,
            disabled: !editFlows,
          }, required('Value')),
        });
      case ActionType.DELETE_:
        return new UntypedFormGroup({
          key: new UntypedFormControl({
            value: formData.key,
            disabled: !editFlows,
          }, required('Key')),
        });
      case ActionType.EXIST:
        return new UntypedFormGroup({
          key: new UntypedFormControl({
            value: formData.key,
            disabled: !editFlows,
          }, required('Key')),
        });
      case ActionType.GET_:
        return new UntypedFormGroup({
          key: new UntypedFormControl({
            value: formData.key,
            disabled: !editFlows,
          }, required('Key')),
        });
      case ActionType.INCREMENT:
        return new UntypedFormGroup({
          key: new UntypedFormControl({
            value: formData.key,
            disabled: !editFlows,
          }, required('Key')),
          expire: new UntypedFormControl({
            value: formData.expire,
            disabled: !editFlows,
          }, [required('Expire'), expiryDaysRange(EXPIRY_MIN_DAYS, EXPIRY_MAX_DAYS)]),
        });
      case ActionType.INCREMENT_BY:
        return new UntypedFormGroup({
          key: new UntypedFormControl({
            value: formData.key,
            disabled: !editFlows,
          }, required('Key')),
          expire: new UntypedFormControl({
            value: formData.expire,
            disabled: !editFlows,
          }, [required('Expire'), expiryDaysRange(EXPIRY_MIN_DAYS, EXPIRY_MAX_DAYS)]),
          value: new UntypedFormControl({
            value: formData.value,
            disabled: !editFlows,
          }, required('Value')),
        });
      case ActionType.SET:
        return new UntypedFormGroup({
          key: new UntypedFormControl({
            value: formData.key,
            disabled: !editFlows,
          }, required('Key')),
          expire: new UntypedFormControl({
            value: formData.expire,
            disabled: !editFlows,
          }, [required('Expire'), expiryDaysRange(EXPIRY_MIN_DAYS, EXPIRY_MAX_DAYS)]),
          value: new UntypedFormControl({
            value: formData.value,
            disabled: !editFlows,
          }, required('Value')),
        });
    }
  }

  get nodeData(): {} {
    return {
      action: this.action,
      'type': 'action',
      metadata: {
        nodeVersion: '1.0.0',
      },
    };
  }

  setNodeData(formData: RedisData, logicData: LogicData) {
    const { name, description, redisAction, yes, no } = formData;

    let actionParams: any = {
      redisAction,
    };

    if (logicData) {
      actionParams = {
        ...actionParams,
        ...this.getRedisAction(redisAction, logicData),
      };
    }

    const result = {
      yes,
      no,
    };

    this.action = {
      ...this.action,
      name,
      description,
      actionParams,
      result,
    };
  }

  getRedisAction(redisAction: string, logicData: LogicData): {} {
    const { key, value } = logicData;

    // Convert days to seconds for backend storage
    const expire = logicData.expire && !isNaN(Number(logicData.expire))
      ? String(Number(logicData.expire) * SECONDS_PER_DAY)
      : logicData.expire;

    const redis1: {} = {
      key,
    };
    const redis2: {} = {
      key,
      expire,
    };
    const redis3: {} = {
      key,
      expire,
      value,
    };

    switch (redisAction) {
      case ActionType.DECREMENT:
        return redis2;
      case ActionType.DECREMENT_BY:
        return redis3;
      case ActionType.DELETE_:
        return redis1;
      case ActionType.EXIST:
        return redis1;
      case ActionType.GET_:
        return redis1;
      case ActionType.INCREMENT:
        return redis2;
      case ActionType.INCREMENT_BY:
        return redis3;
      case ActionType.SET:
        return redis3;
    }
  }
}
